import {
	ArrowRight,
	BookOpenCheck,
	BrainCircuit,
	CheckCircle2,
	ClipboardList,
	GitBranch,
	ShieldAlert,
	Sparkles,
} from "lucide-react";
import type React from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
	evidenceLevelNotes,
	evidenceSources,
	modeTheoryNotes,
	requiredPracticeEvidenceFields,
	requiredQuestionEvidenceFields,
} from "@/data/evidenceCatalog";
import {
	domains,
	MAX_QUESTIONS,
	MIN_QUESTIONS,
	practiceCatalog,
	questions,
	situations,
} from "@/pages/AssessmentPlayground";

const practiceValues = Object.values(practiceCatalog);
const sourceValues = Object.values(evidenceSources);

const theoryLayers = [
	{
		title: "状态识别",
		subtitle: "先判断当下可干预状态，不做诊断",
		body: "用少量问题识别用户现在主要卡在睡眠、压力、行动、自我、关系、身体、秩序、积极生活还是安全风险。入口题只分流，后续题继续确认、排除和加权。",
		icon: <BrainCircuit className="size-5 text-primary" />,
	},
	{
		title: "练习干预",
		subtitle: "把状态翻译成低成本动作",
		body: "每个具体情况都匹配认知、身体、行动、关系、积极、记录或安全支持练习。不是默认冥想，也不是默认感恩，而是按状态选择最短路径。",
		icon: <Sparkles className="size-5 text-primary" />,
	},
	{
		title: "反馈闭环",
		subtitle: "看用户做完有没有变好",
		body: "练习后记录完成情况、前后状态变化、难度、是否有用和是否愿意继续。反馈不好就换练习，反馈稳定才沉淀成训练路径。",
		icon: <GitBranch className="size-5 text-primary" />,
	},
	{
		title: "证据治理",
		subtitle: "每道题和每个练习都有来源边界",
		body: "题目要标记来源、测试状态、评分规则、适用边界和审核状态；练习要说明理论、为什么推荐、为什么不是别的、更好替代和安全边界。",
		icon: <BookOpenCheck className="size-5 text-primary" />,
	},
];

const productPrinciples = [
	"不是单一 PERMA。PERMA 是长期积极生活目标，PHQ/GAD/WHO-5/CBT-I/CBT/正念/行为激活等共同构成识别和练习依据。",
	"不是心理诊断。产品只输出“当前更像哪类可训练状态”，严重风险必须转向真人和专业支持。",
	"不是通用聊天。MVP 先不用 AI，用结构化题目、结构化练习和反馈数据验证用户是否愿意练。",
	"不是纯课程库。课程和练习必须由用户状态触发，并根据反馈调整路径。",
];

const feedbackItems = [
	"练习前心情/压力/能量 1-5 分",
	"练习后同一指标 1-5 分",
	"是否完成，完成了多少",
	"难度是否合适",
	"这次有没有帮助",
	"明天是否愿意继续",
	"用户主动备注：文字、语音或图片可选，不强制",
];

export const TheoryCore: React.FC = () => {
	const evidenceCounts = countBy(practiceValues, (practice) => practice.evidenceLevel);
	const modeCounts = countBy(practiceValues, (practice) => practice.mode);
	const positiveCounts = countBy(practiceValues, (practice) => practice.positiveFit);

	return (
		<div className="space-y-6">
			<section className="rounded-2xl border border-border/70 bg-card p-6 shadow-sm">
				<div className="flex flex-wrap items-start justify-between gap-5">
					<div className="max-w-4xl">
						<div className="flex flex-wrap items-center gap-2">
							<Badge className="bg-primary text-primary-foreground">理论核心</Badge>
							<Badge variant="outline">积极生活轻训练系统</Badge>
						</div>
						<h1 className="mt-4 text-3xl font-semibold tracking-tight text-foreground">
							用小问题识别状态，用小练习建立情绪掌控力
						</h1>
						<p className="mt-3 text-base leading-7 text-muted-foreground">
							我们的核心不是“测完给一堆内容”，而是让用户每天用少量有依据的问题看见自己当下的状态，再用一个低成本动作完成一次可感知的调整，持续反馈后沉淀成个人心理支撑系统。
						</p>
					</div>
					<div className="grid min-w-72 grid-cols-2 gap-3">
						<Metric label="领域" value={Object.keys(domains).length} />
						<Metric label="具体情况" value={Object.keys(situations).length} />
						<Metric label="题目" value={questions.length} />
						<Metric label="练习" value={practiceValues.length} />
					</div>
				</div>
			</section>

			<section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
				{theoryLayers.map((layer) => (
					<Card className="rounded-2xl" key={layer.title}>
						<CardHeader>
							<div className="flex items-center gap-2">
								{layer.icon}
								<Badge variant="secondary">{layer.title}</Badge>
							</div>
							<CardTitle className="text-lg">{layer.subtitle}</CardTitle>
						</CardHeader>
						<CardContent>
							<p className="text-sm leading-6 text-muted-foreground">{layer.body}</p>
						</CardContent>
					</Card>
				))}
			</section>

			<section className="grid gap-5 lg:grid-cols-[1fr_0.85fr]">
				<Card className="rounded-2xl">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-xl">
							<CheckCircle2 className="size-5 text-primary" />
							我们的理论到底是什么
						</CardTitle>
						<CardDescription>可以对外表达成“积极生活轻训练系统”。</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="rounded-2xl border border-primary/25 bg-primary/5 p-5">
							<h2 className="text-xl font-semibold text-foreground">一句话版本</h2>
							<p className="mt-3 text-base leading-7 text-muted-foreground">
								帮助用户识别当下状态，完成一个足够小但有理论支撑的行动，通过反馈逐步建立自己的情绪掌控力和积极生活结构。
							</p>
						</div>
						<div className="grid gap-3">
							{productPrinciples.map((principle) => (
								<div
									className="flex gap-3 rounded-xl border border-border/70 bg-background/80 p-4"
									key={principle}
								>
									<CheckCircle2 className="mt-0.5 size-5 shrink-0 text-primary" />
									<p className="text-sm leading-6 text-foreground">{principle}</p>
								</div>
							))}
						</div>
					</CardContent>
				</Card>

				<Card className="rounded-2xl">
					<CardHeader>
						<CardTitle className="flex items-center gap-2 text-xl">
							<ClipboardList className="size-5 text-primary" />
							为什么是 {MIN_QUESTIONS}-{MAX_QUESTIONS} 题
						</CardTitle>
						<CardDescription>太少显得武断，太多用户放弃。</CardDescription>
					</CardHeader>
					<CardContent className="space-y-3">
						<ReasonBlock
							label="少于 4 题"
							value="容易变成用户自己选标签，比如只选“低落”就下判断，不够可信。"
						/>
						<ReasonBlock
							label="4-6 题"
							value="适合 MVP 首轮体验：入口分流、具体情况确认、强度/持续时间、影响程度。"
						/>
						<ReasonBlock
							label="7-10 题"
							value="用于多个领域同时命中、低落/风险、安全确认、睡眠或关系细分不清的情况。"
						/>
						<ReasonBlock
							label="超过 10 题"
							value="不适合作为轻训练入口，应转成周期性量表或完整评估。"
						/>
					</CardContent>
				</Card>
			</section>

			<section className="grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
				<Card className="rounded-2xl">
					<CardHeader>
						<CardTitle className="text-xl">练习为什么分这些方向</CardTitle>
						<CardDescription>
							用户状态不一样，最短干预路径也不一样，所以不能只有冥想或感恩。
						</CardDescription>
					</CardHeader>
					<CardContent className="grid gap-3">
						{Object.entries(modeTheoryNotes).map(([mode, note]) => (
							<div className="rounded-xl border border-border/70 bg-background/80 p-4" key={mode}>
								<div className="flex flex-wrap items-center gap-2">
									<Badge>{mode}</Badge>
									<Badge variant="outline">{modeCounts[mode] ?? 0} 个练习</Badge>
								</div>
								<p className="mt-3 text-sm font-medium text-foreground">{note.summary}</p>
								<p className="mt-2 text-sm leading-6 text-muted-foreground">{note.reason}</p>
							</div>
						))}
					</CardContent>
				</Card>

				<Card className="rounded-2xl">
					<CardHeader>
						<CardTitle className="text-xl">证据强度与当前盘点</CardTitle>
						<CardDescription>
							不是所有练习都等价。强证据优先，探索项必须靠用户反馈验证。
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="grid gap-3 sm:grid-cols-2">
							{Object.entries(evidenceLevelNotes).map(([level, note]) => (
								<div
									className="rounded-xl border border-border/70 bg-background/80 p-4"
									key={level}
								>
									<div className="flex items-center justify-between gap-3">
										<p className="font-semibold text-foreground">{level}</p>
										<Badge variant="secondary">{evidenceCounts[level] ?? 0}</Badge>
									</div>
									<p className="mt-2 text-sm leading-6 text-muted-foreground">{note}</p>
								</div>
							))}
						</div>
						<div className="rounded-xl border border-border/70 bg-muted/30 p-4">
							<p className="font-semibold text-foreground">积极心理学占比</p>
							<div className="mt-3 flex flex-wrap gap-2">
								<Badge>是：{positiveCounts["是"] ?? 0}</Badge>
								<Badge variant="secondary">部分是：{positiveCounts["部分是"] ?? 0}</Badge>
								<Badge variant="outline">不是：{positiveCounts["不是"] ?? 0}</Badge>
							</div>
							<p className="mt-3 text-sm leading-6 text-muted-foreground">
								这说明产品不是“全都积极心理学”。身体调节、睡眠、CBT
								和安全支持是底座；积极心理学主要负责长期积极生活和正向体验建构。
							</p>
						</div>
					</CardContent>
				</Card>
			</section>

			<section className="grid gap-5 lg:grid-cols-2">
				<Card className="rounded-2xl">
					<CardHeader>
						<CardTitle className="text-xl">题目和练习的证据字段</CardTitle>
						<CardDescription>后续后台录入时，这些字段是必填项。</CardDescription>
					</CardHeader>
					<CardContent className="grid gap-4 md:grid-cols-2">
						<Checklist title="题目必填" items={requiredQuestionEvidenceFields} />
						<Checklist title="练习必填" items={requiredPracticeEvidenceFields} />
					</CardContent>
				</Card>

				<Card className="rounded-2xl">
					<CardHeader>
						<CardTitle className="text-xl">反馈闭环记录什么</CardTitle>
						<CardDescription>
							如果不记录反馈，就无法知道用户是不是真的买账，也无法决定换不换练习。
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="grid gap-3">
							{feedbackItems.map((item) => (
								<div
									className="flex items-center gap-3 rounded-xl border border-border/70 bg-background/80 p-3"
									key={item}
								>
									<CheckCircle2 className="size-4 shrink-0 text-primary" />
									<p className="text-sm text-foreground">{item}</p>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			</section>

			<Card className="rounded-2xl">
				<CardHeader>
					<div className="flex flex-wrap items-center gap-2">
						<ShieldAlert className="size-5 text-primary" />
						<Badge variant="secondary">{sourceValues.length} 个来源</Badge>
					</div>
					<CardTitle className="text-xl">当前理论来源池</CardTitle>
					<CardDescription>
						这些来源用于“维度借鉴”和“练习理论说明”，不是让产品复制量表原题或替代专业评估。
					</CardDescription>
				</CardHeader>
				<CardContent className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
					{sourceValues.map((source) => (
						<a
							className="rounded-2xl border border-border/70 bg-background/80 p-4 transition-colors hover:border-primary"
							href={source.url}
							key={source.id}
							rel="noreferrer"
							target="_blank"
						>
							<div className="flex flex-wrap items-center gap-2">
								<Badge>{source.type}</Badge>
								<Badge variant="outline">{source.title}</Badge>
							</div>
							<p className="mt-3 text-sm leading-6 text-foreground">{source.useFor}</p>
							<p className="mt-3 text-xs leading-5 text-muted-foreground">{source.caution}</p>
							<span className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary">
								查看来源 <ArrowRight className="size-3" />
							</span>
						</a>
					))}
				</CardContent>
			</Card>
		</div>
	);
};

function Metric({ label, value }: { label: string; value: number }) {
	return (
		<div className="rounded-xl border border-border/70 bg-background/80 p-4">
			<p className="text-xs text-muted-foreground">{label}</p>
			<p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
		</div>
	);
}

function ReasonBlock({ label, value }: { label: string; value: string }) {
	return (
		<div className="rounded-xl border border-border/70 bg-background/80 p-4">
			<p className="text-sm font-semibold text-foreground">{label}</p>
			<p className="mt-2 text-sm leading-6 text-muted-foreground">{value}</p>
		</div>
	);
}

function Checklist({ items, title }: { items: string[]; title: string }) {
	return (
		<div className="rounded-xl border border-border/70 bg-background/80 p-4">
			<p className="font-semibold text-foreground">{title}</p>
			<div className="mt-3 grid gap-2">
				{items.map((item) => (
					<div className="flex items-center gap-2 text-sm text-muted-foreground" key={item}>
						<CheckCircle2 className="size-4 shrink-0 text-primary" />
						<span>{item}</span>
					</div>
				))}
			</div>
		</div>
	);
}

function countBy<T>(items: T[], getKey: (item: T) => string) {
	return items.reduce<Record<string, number>>((acc, item) => {
		const key = getKey(item);
		acc[key] = (acc[key] ?? 0) + 1;
		return acc;
	}, {});
}
